import styled from "@emotion/styled"
import { Box, Dialog, DialogContent, IconButton, Typography } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import React, { useEffect, useState } from "react"
import { useTheme } from "@emotion/react"
import { Formik } from "formik"
import { validationSchemaAddWeeklyCategories } from "helpers/validationSchema"
import AppTextField from "components/StyledComponents/AppTextField"
import ImageUploadField from "components/FileUpload/ImageUploadField"
import AppButton from "components/StyledComponents/AppButton"
import ImageCropper from "components/ImageCropper"
import { useDispatch, useSelector } from "react-redux"
import { uploadWeeklyCategoryImageAction } from "redux/superAdmin/actions"
import { addWeeklyCategoryAction, editWeeklyCategoryAction } from "redux/superAdmin/actions/user"
import { superAdminAction } from "redux/superAdmin/slice/user"

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    backgroundColor: "transparent"
  },
  "& .MuiDialogContent-root": {
    backgroundColor: theme.palette.background.default,
    borderRadius: 12,
    border: "none",
    width: 580,
    padding: 35,
    boxSizing: "border-box"
  }
}))
const AddNewCategoryModal = ({ open, onClose, isEdit, data }) => {
  const [uploadType, setUploadType] = useState("")
  const [fileDetails, setFileDetails] = useState(null)
  const [imageObjectURL, setImageObjectUrl] = useState(null)
  const [openCrop, setCrop] = useState(false)
  const { weeklyCategoryImage } = useSelector((store) => store.superAdmin)

  const dispatch = useDispatch()
  const theme = useTheme()

  const styles = {
    closeBtn: {
      color: theme.palette.text.primary,
      position: "absolute",
      top: "10px",
      right: "10px",
      backgroundColor: theme.palette.background.main
    }
  }
  const onImageUpload = (image, type, formik) => {
    setUploadType("")
    if (type === "image") {
      setUploadType("image")
      setCrop(true)
      setFileDetails(image)
      setImageObjectUrl(URL.createObjectURL(image))
    }
    if (type === "icon") {
      setUploadType("icon")
      let data = new FormData()
      data.append("media", image)
      data.append("media_type", "IMAGE")
      data.append("content_type", image.type)
      formik.setFieldValue("categoryIcon", image)
      dispatch(uploadWeeklyCategoryImageAction({ data, type: "icon" }))
    }
  }

  function handleCroppedImage(image, formik) {
    let data = new FormData()
    data.append("media", image)
    data.append("media_type", "IMAGE")
    data.append("content_type", image.type)
    if (uploadType === "image") {
      formik.setFieldValue("categoryImage", image)
    }
    if (uploadType === "icon") {
      formik.setFieldValue("categoryIcon", image)
    }
    dispatch(uploadWeeklyCategoryImageAction({ data, type: uploadType }))
    setUploadType("")
  }

  const handleImageDelete = (formik, type) => {
    if (type === "image") {
      formik.setFieldValue("categoryImage", null)
    }
    if (type === "icon") {
      formik.setFieldValue("categoryIcon", null)
    }
  }

  function showCropperModal(imageObjectURL, fileDetails, formik) {
    return (
      <ImageCropper
        setSelectedImage={(image) => handleCroppedImage(image, formik)}
        srcImage={imageObjectURL}
        fileDetail={fileDetails}
        aspectRatio={uploadType === "image" && 11 / 16}
        onClose={setCrop}
        openCrop={openCrop}></ImageCropper>
    )
  }

  const handleSubmit = (values) => {
    const payload = isEdit
      ? {
          name: values?.categoryName || "",
          image: weeklyCategoryImage?.image || values?.categoryImage?.id || "",
          icon: weeklyCategoryImage?.icon || values?.categoryIcon?.id || ""
        }
      : [
          {
            name: values?.categoryName || "",
            image: values?.categoryImage ? weeklyCategoryImage?.image || "" : "",
            icon: values?.categoryIcon ? weeklyCategoryImage?.icon || "" : ""
          }
        ]
    if (isEdit) {
      dispatch(
        editWeeklyCategoryAction({ payload: payload, onClose: () => onClose(), id: data?.id })
      )
    } else {
      dispatch(addWeeklyCategoryAction({ payload: payload, onClose: () => onClose() }))
    }
  }

  const getInitialValues = () => {
    if (isEdit) {
      return {
        categoryName: data?.name ?? "",
        categoryImage: data?.image_url ? { id: data?.image, url: data?.image_url } : null,
        categoryIcon: data?.icon_url ? { id: data?.icon, url: data?.icon_url } : null
      }
    }

    return {
      categoryName: "",
      categoryImage: null,
      categoryIcon: null
    }
  }

  useEffect(() => {
    dispatch(superAdminAction.setWeeklyCategoryIcon(""))
    dispatch(superAdminAction.setWeeklyCategoryImage(""))
  }, [])

  return (
    <StyledDialog open={open}>
      <DialogContent>
        <Box className="d-flex justify-content-end">
          <IconButton onClick={() => onClose()} sx={styles.closeBtn}>
            <CloseIcon sx={{ height: "14px", width: "14px" }} />
          </IconButton>
        </Box>
        <Box className="w-100 d-flex justify-content-center mb-2">
          <Typography variant="h2">
            {isEdit ? "Edit Weekly Category" : "Add New Weekly Category"}
          </Typography>
        </Box>
        <Formik
          initialValues={getInitialValues()}
          enableReinitialize
          onSubmit={handleSubmit}
          validationSchema={validationSchemaAddWeeklyCategories}>
          {(formik) => (
            <>
              {showCropperModal(imageObjectURL, fileDetails, formik)}
              <Box className="d-flex justify-content-between w-100 py-2 mt-4">
                <AppTextField
                  id="categoryName"
                  name="categoryName"
                  fullWidth
                  isUpperCase={!false}
                  label={`Category`}
                  isRequired={true}
                  placeholder="Enter Category"
                  value={formik.values.categoryName}
                  onChange={formik.handleChange}
                  error={formik.touched.categoryName && formik.errors.categoryName}
                  helperText={formik.errors.categoryName}
                />
              </Box>
              <Box className="d-flex justify-content-between w-100 py-2 mt-1">
                <ImageUploadField
                  id="categoryImage"
                  name="categoryImage"
                  label={"Image"}
                  isVariant={true}
                  placeholder={`Upload Category Image`}
                  value={formik.values.categoryImage}
                  isRequired={true}
                  onChange={(e) => onImageUpload(e, "image", formik)}
                  onDelete={() => handleImageDelete(formik, "image")}
                  error={formik.errors.categoryImage}
                  touched={formik.touched.categoryImage}
                />
              </Box>
              <Box className="d-flex justify-content-between w-100 py-2 mt-1">
                <ImageUploadField
                  id="categoryIcon"
                  name="categoryIcon"
                  label={"Icon"}
                  isVariant={true}
                  placeholder={`Upload Category Icon`}
                  value={formik.values.categoryIcon}
                  onChange={(e) => onImageUpload(e, "icon", formik)}
                  onDelete={() => handleImageDelete(formik, "icon")}
                  error={formik.errors.categoryIcon}
                  touched={formik.touched.categoryIcon}
                />
              </Box>
              <Box className="mt-5 d-flex justify-content-around">
                <AppButton
                  onClick={formik.handleSubmit}
                  sx={{ height: "48px", width: 150 }}
                  variant="outlined">
                  Save
                </AppButton>
                <AppButton
                  onClick={() => onClose()}
                  sx={{ height: "48px", width: 150 }}
                  variant="outlined">
                  Cancel
                </AppButton>
              </Box>
            </>
          )}
        </Formik>
      </DialogContent>
    </StyledDialog>
  )
}
export default AddNewCategoryModal
